import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { keyframes } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { useRequestCountdowns } from "../../hooks/useRequestCountdowns";

// Define the keyframes for the slide-down animation
const slideDown = keyframes`
  0% {
    transform: translateY(-50%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const NewRonRequestBox = () => {
  const notaryRequests = useSelector(
    (state) => state?.notarySession?.notaryRequests,
  );
  const { formatTimeRemaining, getTimeRemaining } = useRequestCountdowns();

  const [currentRequest, setCurrentRequest] = useState(null);
  const [dismissedRequests, setDismissedRequests] = useState(new Set());
  const autoHideTimeoutRef = useRef(null);

  // Load dismissed requests from localStorage on component mount
  useEffect(() => {
    const savedDismissed = localStorage.getItem("dismissedRonRequests");
    if (savedDismissed) {
      try {
        const dismissedArray = JSON.parse(savedDismissed);
        setDismissedRequests(new Set(dismissedArray));
      } catch (error) {
        console.error(
          "Error parsing dismissed requests from localStorage:",
          error,
        );
      }
    }
  }, []);

  // Save dismissed requests to localStorage whenever it changes
  useEffect(() => {
    if (dismissedRequests.size > 0) {
      localStorage.setItem(
        "dismissedRonRequests",
        JSON.stringify([...dismissedRequests]),
      );
    }
  }, [dismissedRequests]);

  useEffect(() => {
    // Automatically set the first request from the list if currentRequest is null
    // and it hasn't been dismissed
    if (!currentRequest && notaryRequests && notaryRequests.length > 0) {
      const firstUndismissedRequest = notaryRequests.find(
        (request) => !dismissedRequests.has(request.id),
      );
      if (firstUndismissedRequest) {
        setCurrentRequest(firstUndismissedRequest);
      }
    }
  }, [notaryRequests, currentRequest, dismissedRequests]);

  const handleDismiss = useCallback(() => {
    // Clear any pending auto-hide timeout
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    // Only hide the popup, don't remove from Redux store
    setDismissedRequests((prev) => new Set([...prev, currentRequest?.id]));
    setCurrentRequest(null);
  }, [currentRequest?.id]);

  // Auto-hide after 15 seconds when a new request is shown
  useEffect(() => {
    if (currentRequest) {
      // Clear any existing timeout
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }

      // Set new timeout for 15 seconds
      autoHideTimeoutRef.current = setTimeout(() => {
        handleDismiss();
      }, 15000);
    }

    // Cleanup timeout on unmount or when currentRequest changes
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, [currentRequest, handleDismiss]);

  // Clean up localStorage when new requests arrive (remove old dismissed requests)
  useEffect(() => {
    if (notaryRequests && notaryRequests.length > 0) {
      const currentRequestIds = new Set(notaryRequests.map((req) => req.id));
      setDismissedRequests((prev) => {
        const filtered = new Set(
          [...prev].filter((id) => currentRequestIds.has(id)),
        );
        return filtered;
      });
    }
  }, [notaryRequests]);

  const handleDismissAll = () => {
    // Clear any pending auto-hide timeout
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    // Mark all current requests as dismissed
    const allRequestIds = notaryRequests.map((request) => request.id);
    setDismissedRequests((prev) => new Set([...prev, ...allRequestIds]));
    setCurrentRequest(null);
  };

  const handleAccept = () => {
    // Clear any pending auto-hide timeout
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    window.open(currentRequest?.accept_url, "_blank");

    // Only hide the popup, don't remove from Redux store
    setDismissedRequests((prev) => new Set([...prev, currentRequest?.id]));
    setCurrentRequest(null);
  };

  // Don't render if no current request or if all requests have been dismissed
  if (
    !currentRequest ||
    notaryRequests.every((request) => dismissedRequests.has(request.id))
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: "20px",
        right: "20px",
        width: "auto",
        backgroundColor: "#f7fcf7",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.4)",
        borderRadius: "8px",
        padding: "16px",
        zIndex: 1000,
        animation: `${slideDown} 0.5s ease-out`,
      }}
    >
      <Box px={2} sx={{ position: "relative" }}>
        <Typography mx={2} align="center" variant="h5" color="primary">
          New Notarization Request
        </Typography>
        <Box sx={{ position: "absolute", right: -20, top: -18 }}>
          <Tooltip title="Dismiss All">
            <IconButton onClick={handleDismissAll}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="body1"
          fontWeight="bold"
          align="center"
          color="primary"
        >
          {currentRequest?.type === "immediate" ? "Expedited" : "Later Date"}
        </Typography>
        <Typography fontWeight="bold" align="center" sx={{ color: "#474747" }}>
          {currentRequest?.date}
        </Typography>

        {/* Request countdown indicator */}
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Request expires in{" "}
            {formatTimeRemaining(getTimeRemaining(currentRequest?.id))}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
          <Button color="error" onClick={handleDismiss}>
            Dismiss
          </Button>
          <Button
            sx={{ width: "10rem" }}
            color="success"
            onClick={handleAccept}
            variant="outlined"
          >
            Accept
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NewRonRequestBox;
